import React, { useState } from 'react';
import { CreditCard, Building, AlertTriangle, PenTool } from 'lucide-react';
import SignatureCapture from './SignatureCapture';
import { 
  validateSABankAccount, 
  validateSABranchCode, 
  validateSABankDetails,
  saBanks,
  accountTypes
} from './validationUtils';

const PaymentStep = ({ formData, updateFormData, errors = {} }) => {
  const [localErrors, setLocalErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    updateFormData({
      ...formData,
      [name]: newValue
    });
    
    // Validate bank account number
    if (name === 'accountNumber') {
      if (value.length > 0 && formData.bank) {
        const result = validateSABankAccount(value, formData.bank);
        if (!result.valid) {
          setLocalErrors(prev => ({ ...prev, accountNumber: result.message }));
          setValidationWarnings(prev => ({ ...prev, accountNumber: null }));
        } else {
          setLocalErrors(prev => ({ ...prev, accountNumber: null }));
          if (result.warning) {
            setValidationWarnings(prev => ({ ...prev, accountNumber: result.warning }));
          } else {
            setValidationWarnings(prev => ({ ...prev, accountNumber: null }));
          }
        }
      } else {
        setLocalErrors(prev => ({ ...prev, accountNumber: null }));
        setValidationWarnings(prev => ({ ...prev, accountNumber: null }));
      }
    }
    
    // Validate branch code
    if (name === 'branchCode') {
      if (value.length > 0 && formData.bank) {
        const result = validateSABranchCode(value, formData.bank);
        if (!result.valid) {
          setLocalErrors(prev => ({ ...prev, branchCode: result.message }));
          setValidationWarnings(prev => ({ ...prev, branchCode: null }));
        } else {
          setLocalErrors(prev => ({ ...prev, branchCode: null }));
          if (result.warning) {
            setValidationWarnings(prev => ({ ...prev, branchCode: result.warning }));
          } else {
            setValidationWarnings(prev => ({ ...prev, branchCode: null }));
          }
        }
      } else {
        setLocalErrors(prev => ({ ...prev, branchCode: null }));
        setValidationWarnings(prev => ({ ...prev, branchCode: null }));
      }
    }
    
    // When bank changes, validate account number and branch code if they exist
    if (name === 'bank' && value) {
      if (formData.accountNumber) {
        const accountResult = validateSABankAccount(formData.accountNumber, value);
        if (!accountResult.valid) {
          setLocalErrors(prev => ({ ...prev, accountNumber: accountResult.message }));
          setValidationWarnings(prev => ({ ...prev, accountNumber: null }));
        } else {
          setLocalErrors(prev => ({ ...prev, accountNumber: null }));
          if (accountResult.warning) {
            setValidationWarnings(prev => ({ ...prev, accountNumber: accountResult.warning }));
          } else {
            setValidationWarnings(prev => ({ ...prev, accountNumber: null }));
          }
        }
      }
      
      if (formData.branchCode) {
        const branchResult = validateSABranchCode(formData.branchCode, value);
        if (!branchResult.valid) {
          setLocalErrors(prev => ({ ...prev, branchCode: branchResult.message }));
          setValidationWarnings(prev => ({ ...prev, branchCode: null }));
        } else {
          setLocalErrors(prev => ({ ...prev, branchCode: null }));
          if (branchResult.warning) {
            setValidationWarnings(prev => ({ ...prev, branchCode: branchResult.warning }));
          } else {
            setValidationWarnings(prev => ({ ...prev, branchCode: null }));
          }
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Payment Details</h2>
      <p className="text-gray-400 text-sm">Please provide your banking details for monthly payments.</p>
      
      {/* Banking Details */}
      <div className="space-y-4">
        {/* Bank and Account Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="bank" className="block text-sm font-medium text-gray-300">Bank</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-4 w-4 text-gray-400" />
              </div>
              <select
                id="bank"
                name="bank"
                value={formData.bank || ''}
                onChange={handleChange}
                className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                  errors.bank ? 'border-red-500' : 'border-gray-600'
                } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              >
                <option value="">Select your bank</option>
                {saBanks.map((bank) => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>
            {errors.bank && <p className="text-red-500 text-xs mt-1">{errors.bank}</p>}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="accountType" className="block text-sm font-medium text-gray-300">Type of Account</label>
            <select
              id="accountType"
              name="accountType"
              value={formData.accountType || ''}
              onChange={handleChange}
              className={`w-full py-2 px-3 bg-gray-700 border ${
                errors.accountType ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
            >
              <option value="">Select account type</option>
              {accountTypes.map((type) => (
                <option key={type} value={type.toLowerCase()}>{type}</option>
              ))}
            </select>
            {errors.accountType && <p className="text-red-500 text-xs mt-1">{errors.accountType}</p>}
          </div>
        </div>
        
        {/* Account Number */}
        <div className="space-y-2">
          <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-300">Account Number</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CreditCard className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber || ''}
              onChange={handleChange}
              className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                errors.accountNumber || localErrors.accountNumber ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="Your bank account number"
            />
          </div>
          {(errors.accountNumber || localErrors.accountNumber) && (
            <p className="text-red-500 text-xs mt-1">{errors.accountNumber || localErrors.accountNumber}</p>
          )}
          {validationWarnings.accountNumber && (
            <p className="text-amber-400 text-xs mt-1">Note: {validationWarnings.accountNumber}</p>
          )}
        </div>
        
        {/* Account Holder Name */}
        <div className="space-y-2">
          <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-300">Account Holder Name</label>
          <input
            type="text"
            id="accountHolderName"
            name="accountHolderName"
            value={formData.accountHolderName || ''}
            onChange={handleChange}
            className={`w-full py-2 px-3 bg-gray-700 border ${
              errors.accountHolderName ? 'border-red-500' : 'border-gray-600'
            } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
            placeholder="Name as it appears on your bank account"
          />
          {errors.accountHolderName && <p className="text-red-500 text-xs mt-1">{errors.accountHolderName}</p>}
        </div>
        
        {/* Branch Name and Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="branchName" className="block text-sm font-medium text-gray-300">Branch Name</label>
            <input
              type="text"
              id="branchName"
              name="branchName"
              value={formData.branchName || ''}
              onChange={handleChange}
              className={`w-full py-2 px-3 bg-gray-700 border ${
                errors.branchName ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="Your bank branch name"
            />
            {errors.branchName && <p className="text-red-500 text-xs mt-1">{errors.branchName}</p>}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="branchCode" className="block text-sm font-medium text-gray-300">Branch Code</label>
            <input
              type="text"
              id="branchCode"
              name="branchCode"
              value={formData.branchCode || ''}
              onChange={handleChange}
              className={`w-full py-2 px-3 bg-gray-700 border ${
                errors.branchCode || localErrors.branchCode ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="6-digit branch code"
              maxLength={6}
            />
            {(errors.branchCode || localErrors.branchCode) && (
              <p className="text-red-500 text-xs mt-1">{errors.branchCode || localErrors.branchCode}</p>
            )}
            {validationWarnings.branchCode && (
              <p className="text-amber-400 text-xs mt-1">Note: {validationWarnings.branchCode}</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Debit Order Authorisation */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <h3 className="text-md font-medium text-gray-300">Debit Order Authorisation</h3>
        
        <div className="bg-gray-800 p-4 rounded-lg text-sm text-gray-300 leading-relaxed">
          <p className="mb-3">
            I/We hereby authorise and instruct the Service Provider to draw against my/our account with the above-mentioned bank (or any other bank or branch to which I/we may transfer my/our account) the monthly subscription amount for the service(s) as indicated in this agreement.
          </p>
          <p className="mb-3">
            All such withdrawals from my/our bank account by the Service Provider shall be treated as though they had been signed by me/us personally. I/We agree to pay any bank charges relating to this debit order instruction.
          </p>
          <p className="mb-3">
            This authority may be cancelled by me/us by giving the Service Provider 30 days' notice in writing, but I/we understand that I/we shall not be entitled to any refund of amounts which the Service Provider has withdrawn while this authority was in force.
          </p>
          <p>
            Receipt of this instruction by the Service Provider shall be regarded as receipt thereof by my/our bank.
          </p>
        </div>
        
        <div className="flex items-start mt-2">
          <div className="flex items-center h-5">
            <input
              id="authoriseDebitOrder"
              name="authoriseDebitOrder"
              type="checkbox"
              checked={formData.authoriseDebitOrder || false}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700 rounded"
            />
          </div>
          <label htmlFor="authoriseDebitOrder" className="ml-3 text-sm text-gray-300">
            I authorise the debit order as specified above
          </label>
        </div>
        {errors.authoriseDebitOrder && (
          <p className="text-red-500 text-xs mt-1">{errors.authoriseDebitOrder}</p>
        )}
      </div>
      
      {/* Declaration and Acceptance */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <h3 className="text-md font-medium text-gray-300">Declaration and Acceptance</h3>
        
        <div className="bg-gray-800 p-4 rounded-lg text-sm text-gray-300 leading-relaxed">
          <p className="mb-3">
            I hereby declare that all information provided in this application is true and correct to the best of my knowledge. I understand that providing false information may result in the rejection of my application or termination of services.
          </p>
          <p className="mb-3">
            I acknowledge that I have read, understood, and agree to be bound by the General Terms and Conditions and Service Terms of the Service Provider, which are available at <a href="/terms-and-conditions" className="text-purple-400 hover:text-purple-300 underline" target="_blank" rel="noopener noreferrer">terms-and-conditions</a>.
          </p>
          <p>
            I consent to the Service Provider processing my personal information in accordance with applicable data protection laws for the purposes of providing the services requested.
          </p>
        </div>
        
        <div className="flex items-start mt-2">
          <div className="flex items-center h-5">
            <input
              id="agreeToTerms"
              name="agreeToTerms"
              type="checkbox"
              checked={formData.agreeToTerms || false}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700 rounded"
            />
          </div>
          <label htmlFor="agreeToTerms" className="ml-3 text-sm text-gray-300">
            Yes, I agree to the General & Service Terms
          </label>
        </div>
        {errors.agreeToTerms && (
          <p className="text-red-500 text-xs mt-1">{errors.agreeToTerms}</p>
        )}
      </div>
      
      {/* Signature Capture */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <h3 className="text-md font-medium text-gray-300 flex items-center">
          <PenTool className="h-4 w-4 text-purple-400 mr-2" />
          Your Signature <span className="text-red-400 ml-1">*</span>
        </h3>
        <p className="text-sm text-gray-400">Please sign below to confirm your agreement to the terms and conditions.</p>
        
        <SignatureCapture
          value={formData.signature}
          onChange={(signatureData) => updateFormData({ ...formData, signature: signatureData })}
          error={errors.signature}
        />
      </div>
      
      {/* Important Notice */}
      <div className="bg-amber-900/30 border border-amber-800/30 rounded-lg p-4 mt-6">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mr-3" />
          <div>
            <h4 className="text-amber-400 font-medium text-sm">Important Notice</h4>
            <p className="text-amber-200/80 text-xs mt-1">
              By submitting this application, you are agreeing to a month-to-month contract for the selected package. 
              Your first payment will include any applicable installation or shipping fees.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;